import { useState } from "react";
import { supabase, isSupabaseEnabled } from "../../lib/supabase";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";

const PRICE_IDS = {
  monthly: "price_1TII34AEhiPAT6YGrre9u6oC",
  annual: "price_1TJ1kzAEhiPAT6YGNfJ1Jds1",
};

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToBilling: () => void;
  reason: "property_limit" | "ai_feature" | "trial_expired";
  userId?: string;
}

const CONTENT = {
  property_limit: {
    title: "Subscribe to add unlimited properties",
    body: "Your trial has ended. Subscribe to the Investor plan to add unlimited properties and access all features.",
  },
  ai_feature: {
    title: "Subscribe to access AI features",
    body: "Your trial has ended. Subscribe to the Investor plan to use AI chat, strategy narratives, and the import wizard.",
  },
  trial_expired: {
    title: "Your 14-day trial has ended",
    body: "Subscribe to AusProp Investor to continue editing your portfolio. Your data is safe and waiting for you.",
  },
};

const INVESTOR_FEATURES = [
  "Unlimited properties",
  "AI chat assistant",
  "AI import wizard",
  "Strategy narrative & contextual summaries",
  "Bank feeds (coming soon)",
  "Priority support",
  "All future features",
];

export default function UpgradeModal({
  isOpen,
  onClose,
  onNavigateToBilling,
  reason,
  userId,
}: UpgradeModalProps) {
  const [billing, setBilling] = useState<"monthly" | "annual">("monthly");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const promoCodesEnabled = useFeatureFlag("promo_codes");

  if (!isOpen) return null;
  const content = CONTENT[reason];

  async function handleUpgrade() {
    setError(null);
    if (!isSupabaseEnabled() || !supabase) {
      setError("Sign in required to upgrade.");
      return;
    }
    if (!userId) {
      setError("Could not identify your account. Please refresh and try again.");
      return;
    }
    setLoading(true);
    try {
      const body: { priceId: string; userId: string; couponCode?: string } = {
        priceId: PRICE_IDS[billing],
        userId,
      };
      if (appliedCode) body.couponCode = appliedCode;
      const { data, error: invokeError } = await supabase.functions.invoke("stripe-checkout", {
        body,
      });
      if (invokeError) throw new Error(invokeError.message ?? "Checkout failed");
      const url = (data as { url?: string })?.url;
      if (!url) throw new Error("No checkout URL returned");
      window.location.href = url;
    } catch (err) {
      console.error("[UpgradeModal] checkout error:", err);
      setError("Could not start checkout. Please try again.");
      setLoading(false);
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel — slides up from bottom on mobile, centered modal on desktop */}
      <div className="fixed inset-x-0 bottom-0 z-50 md:inset-0 md:flex md:items-center md:justify-center md:p-4">
        <div className="w-full rounded-t-3xl bg-white px-6 pb-8 pt-4 shadow-2xl md:max-w-md md:rounded-3xl md:p-8">

          {/* Mobile drag handle */}
          <div className="mb-5 flex justify-center md:hidden">
            <div className="h-1 w-8 rounded-full bg-neutral-200" />
          </div>

          {/* Desktop close button */}
          <div className="mb-5 hidden justify-end md:flex">
            <button
              onClick={onClose}
              className="rounded-lg p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 transition-colors"
              aria-label="Close"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="space-y-5">
            {/* Icon */}
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-neutral-900">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>

            {/* Heading + body */}
            <div>
              <h2 className="text-xl font-bold text-neutral-900">{content.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-neutral-600">{content.body}</p>
            </div>

            {/* Billing period toggle */}
            <div className="flex items-center gap-2 rounded-full bg-neutral-100 p-1 w-fit">
              <button
                onClick={() => setBilling("monthly")}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  billing === "monthly"
                    ? "bg-[#C47A2F] text-white"
                    : "text-neutral-600 hover:text-neutral-900"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBilling("annual")}
                className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  billing === "annual"
                    ? "bg-[#C47A2F] text-white"
                    : "text-neutral-600 hover:text-neutral-900"
                }`}
              >
                Annual
                {billing !== "annual" && (
                  <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-xs font-semibold text-emerald-700">
                    Save $78
                  </span>
                )}
              </button>
            </div>

            {/* Promo code — behind feature flag */}
            {promoCodesEnabled && (
              <div className="space-y-2">
                {!promoOpen ? (
                  <button
                    onClick={() => setPromoOpen(true)}
                    className="text-xs text-neutral-400 hover:text-neutral-600 underline underline-offset-2 transition-colors"
                  >
                    Have a promo code?
                  </button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                        placeholder="BETA50"
                        maxLength={20}
                        className="flex-1 rounded-lg border border-neutral-200 px-3 py-2 text-sm font-mono tracking-wider text-neutral-900 placeholder-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-300"
                      />
                      <button
                        onClick={() => {
                          const code = promoInput.trim().toUpperCase();
                          if (code) { setAppliedCode(code); setPromoInput(""); }
                        }}
                        disabled={!promoInput.trim()}
                        className="rounded-lg bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700 disabled:opacity-40 transition-colors"
                      >
                        Apply
                      </button>
                    </div>
                    {appliedCode && (
                      <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                        Code applied: {appliedCode}
                        <button onClick={() => { setAppliedCode(""); setPromoInput(""); }} className="ml-1 text-neutral-400 hover:text-neutral-600">×</button>
                      </div>
                    )}
                    <p className="text-xs text-neutral-400">Discount applied at checkout.</p>
                  </div>
                )}
              </div>
            )}

            {/* Feature list */}
            <div className="rounded-2xl bg-neutral-50 px-4 py-4 space-y-2.5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-neutral-400">
                  Investor plan
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-neutral-900">
                    {billing === "monthly" ? "$39/mo" : "$390/yr"}
                  </span>
                  {billing === "annual" && (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                      Save $78
                    </span>
                  )}
                </div>
              </div>
              {INVESTOR_FEATURES.map((f) => (
                <div key={f} className="flex items-center gap-2.5 text-sm text-neutral-700">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    className="shrink-0 text-emerald-500"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {f}
                </div>
              ))}
            </div>

            {/* CTAs */}
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              onClick={() => void handleUpgrade()}
              disabled={loading}
              className="w-full rounded-xl bg-neutral-900 px-4 py-3.5 text-sm font-semibold text-white hover:bg-neutral-700 transition-colors disabled:opacity-60"
            >
              {loading
                ? "Opening checkout…"
                : billing === "monthly"
                ? "Upgrade to Investor — $39/month"
                : "Upgrade to Investor — $390/year (2 months free)"}
            </button>
            <button
              onClick={onNavigateToBilling}
              className="w-full rounded-xl border border-neutral-200 px-4 py-3 text-sm font-medium text-neutral-600 hover:bg-neutral-50 transition-colors"
            >
              View billing details
            </button>
            <button
              onClick={onClose}
              className="w-full text-sm text-neutral-400 hover:text-neutral-600 transition-colors"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
